/**
 * The screens from before agents that are still used inside the new ones (a worker's card, an order's
 * card, the timeline of what an answer did, the pages not rebuilt yet) read their own older store.
 * This keeps that store in step with the new one, and turns what those screens ask for — "open this
 * conversation", "put these words in the composer", "show this worker" — into the new screen's moves.
 */
import { api } from '../lib/api'
import { useStore } from '../store'
import { fillComposer } from './Agents'
import { useTabs } from './Page'
import { useDemo } from './store'

const SETTINGS_TAB: Record<string, string> = { skills: 'general', mcp: 'apps', devices: 'apps', broker: 'apps', robinhood: 'apps', trading: 'trading', automations: 'general', context: 'usage' }

export function startLegacyBridge() {
  const old = useStore
  const sync = () => {
    const s = useDemo.getState()
    const o = old.getState()
    if (o.user !== s.user || o.settings !== s.settings || o.health !== s.health || o.ready !== s.ready) old.setState({ user: s.user, settings: s.settings, health: s.health, ready: s.ready, theme: 'dark' })
  }
  sync()
  useDemo.subscribe(sync)

  /** The conversations the older cards may send you to, and what is recorded by hand in the portfolio. */
  const load = () => {
    if (!useDemo.getState().user) return
    void api.listChats().then((chats) => old.setState({ chats }), () => {})
    void old.getState().loadPortfolio()
    void old.getState().loadIntegrations()
  }
  let was: string | null = null
  useDemo.subscribe((s) => {
    const id = s.user?.id ?? null
    if (id !== was) ((was = id), load())
    // A worker opened from a link or a card of the new screen opens in the older workers page too.
    if (s.view === 'workers' && s.workerId !== old.getState().workerFocus) old.setState({ workerFocus: s.workerId })
  })
  load()

  const words = (text: string) => {
    old.setState({ composerPrefill: { text, nonce: Date.now() } })
    const s = useDemo.getState()
    // Somewhere without a composer (the workers page, the portfolio): take the words to the main agent.
    if (s.view !== 'agents' && !(s.view === 'chats' && s.tempId)) {
      const main = s.agents.find((a) => a.kind === 'lead') ?? s.agents[0]
      if (main) s.openSession(main.id, null)
    }
    window.setTimeout(() => fillComposer(text), 80)
  }
  old.setState({
    selectChat: async (id: string) => {
      await useDemo.getState().openChat(id)
    },
    prefillComposer: words,
    openWorker: (id: string | null) => (old.setState({ workerFocus: id }), useDemo.setState({ view: 'workers', workerId: id, navOpen: false })),
    focusSettings: (which: string | null) => {
      useDemo.getState().go(which && SETTINGS_TAB[which] === 'apps' ? 'apps' : 'settings')
      if (which && SETTINGS_TAB[which] !== 'apps') useTabs.getState().set({ settings: SETTINGS_TAB[which] ?? 'general' })
    },
    updateSettings: async (p: Parameters<ReturnType<typeof useDemo.getState>['saveSettings']>[0]) => {
      await useDemo.getState().saveSettings(p)
    },
    setView: (v: string) => useDemo.getState().go(v === 'chat' ? 'agents' : v === 'tasks' ? 'agents' : (v as 'portfolio' | 'workers' | 'settings')),
    toggleMemory: () => (useDemo.getState().go('settings'), useTabs.getState().set({ settings: 'memory' })),
    // A $TICKER pressed in an answer opens the stock's own page (candles, your lines, the company).
    openSymbol: (symbol: string) => useDemo.getState().openStock(symbol),
  } as never)
}
